import { useEffect, useState } from 'react';

import { IntlShape, useIntl } from 'react-intl';

import { useUserData } from 'context';
import { ROLE_NAME } from 'enums';
import { reportError } from 'lib/reportError';
import { AccountApi } from 'services/account';
import { IKnowledgeAreaLevelConfirmation } from 'services/knowledge-area-level-confirmation';
import { getFullName } from 'utils/account/getFullName';

// Share concurrent lookups across indicators, without retaining account data
// across profile visits or authentication sessions.
const pendingNames = new Map<number, Promise<string>>();
// Only admins and mentors may read other people's accounts; the backend answers
// everyone else with 403, so they see the date alone.
const canReadOtherAccounts = (role?: string): boolean =>
  role === ROLE_NAME.ADMIN || role === ROLE_NAME.MENTOR;

const getConfirmerName = (id: number): Promise<string> => {
  const pending = pendingNames.get(id);
  if (pending) return pending;
  const request = new AccountApi()
    .getAccount(id, { suppressForbiddenEvent: true })
    .then(account => getFullName(account.firstName, account.lastName).trim())
    .catch(error => {
      reportError(error, 'useLevelConfirmationTooltip.getConfirmerName');
      return '';
    })
    .finally(() => pendingNames.delete(id));
  pendingNames.set(id, request);
  return request;
};

const formatLevelConfirmationTooltip = (
  intl: IntlShape,
  confirmation: IKnowledgeAreaLevelConfirmation | null | undefined,
  name: string,
): string => {
  const timestamp = confirmation?.confirmed ? confirmation.timestamp : null;
  const date = timestamp ? new Date(timestamp) : null;
  const details = [
    confirmation?.confirmed ? name.trim() : '',
    date && !Number.isNaN(date.getTime())
      ? intl.formatDate(date, { day: 'numeric', month: 'short', year: 'numeric' })
      : '',
  ]
    .filter(Boolean)
    .join(', ');
  return intl.formatMessage(
    {
      id: details
        ? 'profile.knowledge-area-details.completion.confirmed-details'
        : 'profile.knowledge-area-details.completion.confirmed',
    },
    { details },
  );
};

const useLevelConfirmationTooltip = (
  confirmation: IKnowledgeAreaLevelConfirmation | null | undefined,
): string => {
  const intl = useIntl();
  const { user } = useUserData();
  const canLookUpConfirmer = canReadOtherAccounts(user?.role.name);
  const confirmerId = confirmation?.confirmed ? confirmation.confirmedBy : null;
  const [confirmer, setConfirmer] = useState<{ id: number; name: string } | null>(null);
  useEffect(() => {
    let active = true;
    if (canLookUpConfirmer && confirmerId !== null && confirmerId !== undefined) {
      getConfirmerName(confirmerId).then(name => {
        if (active) setConfirmer({ id: confirmerId, name });
      });
    }
    return () => {
      active = false;
    };
  }, [canLookUpConfirmer, confirmerId]);
  return formatLevelConfirmationTooltip(
    intl,
    confirmation,
    confirmer?.id === confirmerId ? confirmer.name : '',
  );
};

export { formatLevelConfirmationTooltip, useLevelConfirmationTooltip };
